import React, { useState, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { FilmDetail, ShortVideoPopup, ProductFramePopup, EmbeddedVideoPlayer, ShortSwapPopup } from '../components';
import { FooterContainer } from '../containers/footer';
import { useFilmDetail, useCategoriesAndVideos } from '../hooks';
import * as ROUTES from '../constants/routes';
import productData from '../data/product.json';

export default function FilmDetailPage() {
  const { id } = useParams();
  const history = useHistory();
  const { film, loading, error } = useFilmDetail(id);
  const { videos } = useCategoriesAndVideos();
  const [showShortVideoPopup, setShowShortVideoPopup] = useState(false);
  const [selectedShortVideo, setSelectedShortVideo] = useState(null);
  const [showProductFramePopup, setShowProductFramePopup] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [showShortSwapPopup, setShowShortSwapPopup] = useState(false);
  const [selectedShort, setSelectedShort] = useState(null);

  // Get all unique products for the current film
  const allProducts = useMemo(() => {
    const videoEntry = productData.find((entry) => entry.video === id);
    if (!videoEntry || !videoEntry.product) return [];

    // Create a map to store unique products by URL
    const uniqueProductsMap = new Map();

    videoEntry.product.forEach((product) => {
      const key = product.product_url || product.title;
      if (!uniqueProductsMap.has(key)) {
        uniqueProductsMap.set(key, product);
      }
    });

    return Array.from(uniqueProductsMap.values());
  }, [id]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          color: 'white',
          fontSize: '20px',
        }}
      >
        Đang tải phim...
      </div>
    );
  }

  if (error || !film) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          color: 'white',
          fontSize: '20px',
          gap: '20px',
        }}
      >
        <p>
          <span role="img" aria-label="error">
            ❌
          </span>{' '}
          {error || 'Không tìm thấy phim'}
        </p>
        <button
          type="button"
          onClick={() => history.push(ROUTES.HOME)}
          style={{
            padding: '10px 20px',
            background: '#e50914',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Quay lại trang chủ
        </button>
      </div>
    );
  }

  // Get related films from the same categories or themes
  const relatedFilms = videos
    ? videos
        .filter((video) => {
          // Don't include the current film
          if (video.slug === id) return false;

          // Match by categories or themes
          const hasMatchingCategory = video.categories?.some((cat) => film.categories?.includes(cat));
          const hasMatchingTheme = video.theme?.some((theme) => film.theme?.includes(theme));

          return hasMatchingCategory || hasMatchingTheme;
        })
        .slice(0, 6)
    : [];

  const handleShortVideoClick = (video) => {
    setSelectedShortVideo(video);
    setShowShortVideoPopup(true);
  };

  const handleFrameClick = (frame) => {
    setSelectedFrame(frame);
    setShowProductFramePopup(true);
  };

  // Format duration from minutes to hours and minutes
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Format timestamp from seconds to mm:ss
  const formatTimestamp = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  };

  // Get banner image - use first thumbnail or fallback
  const bannerImage = film.thumbnail || '/images/misc/home-bg.jpg';

  // Get thumbnail for cards
  const getThumbnail = (video) => video.thumbnail || '/images/misc/home-bg.jpg';

  return (
    <>
      <FilmDetail>
        <FilmDetail.Content>
          <FilmDetail.BackButton onClick={() => history.push(ROUTES.HOME)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M19 12H5M5 12L12 19M5 12L12 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Quay lại</span>
          </FilmDetail.BackButton>

          <FilmDetail.MainContent>
            <FilmDetail.InfoColumn>
              <FilmDetail.Header>
                <FilmDetail.Title>{film.title}</FilmDetail.Title>
                <FilmDetail.SubTitle>{film.theme?.join(' • ') || 'Phim'}</FilmDetail.SubTitle>
                <FilmDetail.MetaInfo>
                  <FilmDetail.MetaItem className="rating">
                    ★ {film.rating?.score || 'N/A'} / 10 ({film.rating?.totalVotes?.toLocaleString() || 0} lượt)
                  </FilmDetail.MetaItem>
                  <FilmDetail.MetaItem className="year">
                    {film.releaseDate ? new Date(film.releaseDate).getFullYear() : '2024'}
                  </FilmDetail.MetaItem>
                  <FilmDetail.MetaItem className="duration">{formatDuration(film.duration || 0)}</FilmDetail.MetaItem>
                  <FilmDetail.MetaItem className="country">{film.country || 'Việt Nam'}</FilmDetail.MetaItem>
                  <FilmDetail.MetaItem className="views">
                    <span role="img" aria-label="views" /> {film.views?.toLocaleString() || 0} lượt xem
                  </FilmDetail.MetaItem>
                </FilmDetail.MetaInfo>

                <FilmDetail.Tags>
                  {film.categories?.map((cat, idx) => (
                    <FilmDetail.Tag key={idx}>{cat}</FilmDetail.Tag>
                  ))}
                  {film.theme?.map((theme, idx) => (
                    <FilmDetail.Tag key={`theme-${idx}`}>{theme}</FilmDetail.Tag>
                  ))}
                  {film.ageRating?.code && <FilmDetail.Tag>{film.ageRating.code}</FilmDetail.Tag>}
                  {/* {film.language?.subtitles?.map((lang, idx) => (
                <FilmDetail.Tag key={`sub-${idx}`}>Phụ đề {lang}</FilmDetail.Tag>
              ))} */}
                </FilmDetail.Tags>

                {/* Video Player */}
                {film.videoUrl && (
                  <div
                    style={{
                      marginTop: '30px',
                      marginBottom: '30px',
                      width: '100%',
                    }}
                  >
                    <EmbeddedVideoPlayer film={film} />
                  </div>
                )}

                <FilmDetail.Description>{film.synopsis || film.description}</FilmDetail.Description>

                {/* Director */}
                {film.director && (
                  <div style={{ marginTop: '40px' }}>
                    <h3
                      style={{
                        color: 'white',
                        fontSize: '24px',
                        fontWeight: '700',
                        marginBottom: '25px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Đạo diễn
                    </h3>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '25px',
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
                        padding: '30px',
                        borderRadius: '16px',
                        border: '1px solid rgba(255,255,255,0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                        maxWidth: '600px',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-5px)';
                        e.currentTarget.style.background =
                          'linear-gradient(135deg, rgba(21,215,27,0.2) 0%, rgba(255,255,255,0.05) 100%)';
                        e.currentTarget.style.borderColor = 'rgba(21,215,27,0.5)';
                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(21,215,27,0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.background =
                          'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)';
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
                      }}
                    >
                      {/* Avatar */}
                      <div
                        style={{
                          width: '120px',
                          height: '120px',
                          minWidth: '120px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          border: '3px solid rgba(9, 229, 57, 0.5)',
                          boxShadow: '0 4px 15px rgba(9, 229, 9, 0.3)',
                          background: 'linear-gradient(135deg, #15D71B 0%, #07B26B 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {film.director.avatar ? (
                          <img
                            src={film.director.avatar}
                            alt={film.director.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                            onError={(e) => {
                              // Fallback to initials if image fails to load
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `<div style="color: white; font-size: 40px; font-weight: bold;">${film.director.name.charAt(
                                0
                              )}</div>`;
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              color: 'white',
                              fontSize: '40px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                            }}
                          >
                            {film.director.name.charAt(0)}
                          </div>
                        )}
                      </div>

                      {/* Director Info */}
                      <div
                        style={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                        }}
                      >
                        <div
                          style={{
                            color: 'white',
                            fontWeight: '700',
                            fontSize: '20px',
                            lineHeight: '1.3',
                          }}
                        >
                          {film.director.name}
                        </div>
                        <div
                          style={{
                            color: '#15D71B',
                            fontSize: '13px',
                            fontWeight: '600',
                            background: 'rgba(21,215,27,0.15)',
                            padding: '6px 14px',
                            borderRadius: '12px',
                            display: 'inline-block',
                            border: '1px solid rgba(21,215,27,0.3)',
                            width: 'fit-content',
                            letterSpacing: '0.5px',
                          }}
                        >
                          DIRECTOR
                        </div>
                        {film.director.bio && (
                          <div
                            style={{
                              color: '#ccc',
                              fontSize: '14px',
                              lineHeight: '1.6',
                              marginTop: '5px',
                            }}
                          >
                            {film.director.bio}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Cast & Crew */}
                {film.cast && film.cast.length > 0 && (
                  <div style={{ marginTop: '40px' }}>
                    <h3
                      style={{
                        color: 'white',
                        fontSize: '24px',
                        fontWeight: '700',
                        marginBottom: '25px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Diễn viên
                    </h3>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '20px',
                        marginBottom: '10px',
                      }}
                    >
                      {film.cast.map((actor, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px',
                            background:
                              'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
                            padding: '20px',
                            borderRadius: '16px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-5px)';
                            e.currentTarget.style.background =
                              'linear-gradient(135deg, rgba(21,215,27,0.2) 0%, rgba(255,255,255,0.05) 100%)';
                            e.currentTarget.style.borderColor = 'rgba(21,215,27,0.5)';
                            e.currentTarget.style.boxShadow = '0 8px 25px rgba(21,215,27,0.3)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.background =
                              'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)';
                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
                          }}
                        >
                          {/* Avatar */}
                          <div
                            style={{
                              width: '100px',
                              height: '100px',
                              borderRadius: '50%',
                              overflow: 'hidden',
                              border: '3px solid rgba(21,215,27,0.5)',
                              boxShadow: '0 4px 15px rgba(21,215,27,0.3)',
                              background: 'linear-gradient(135deg, #15D71B 0%, #07B26B 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {actor.avatar ? (
                              <img
                                src={actor.avatar}
                                alt={actor.name}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                                onError={(e) => {
                                  // Fallback to initials if image fails to load
                                  e.target.style.display = 'none';
                                  e.target.parentElement.innerHTML = `<div style="color: white; font-size: 32px; font-weight: bold;">${actor.name.charAt(
                                    0
                                  )}</div>`;
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  color: 'white',
                                  fontSize: '32px',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase',
                                }}
                              >
                                {actor.name.charAt(0)}
                              </div>
                            )}
                          </div>

                          {/* Actor Info */}
                          <div
                            style={{
                              textAlign: 'center',
                              width: '100%',
                            }}
                          >
                            <div
                              style={{
                                color: 'white',
                                fontWeight: '700',
                                fontSize: '16px',
                                marginBottom: '6px',
                                lineHeight: '1.3',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                padding: '0 5px',
                              }}
                            >
                              {actor.name}
                            </div>
                            <div
                              style={{
                                color: '#15D71B',
                                fontSize: '13px',
                                fontWeight: '500',
                                background: 'rgba(21,215,27,0.15)',
                                padding: '4px 12px',
                                borderRadius: '12px',
                                display: 'inline-block',
                                border: '1px solid rgba(21,215,27,0.3)',
                              }}
                            >
                              {actor.role}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Awards */}
                {/* {film.awards && film.awards.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h3 style={{ color: 'white', fontSize: '20px', marginBottom: '10px' }}>
                  <span role="img" aria-label="award">🏆</span> Giải thưởng
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {film.awards.map((award, idx) => (
                    <div key={idx} style={{
                      color: '#ffd700',
                      padding: '8px 12px',
                      background: 'rgba(255,215,0,0.1)',
                      borderRadius: '4px',
                      borderLeft: '3px solid #ffd700'
                    }}>
                      {award}
                    </div>
                  ))}
                </div>
              </div>
            )} */}
              </FilmDetail.Header>

              {/* Highlights */}
              {film.highlights && film.highlights.length > 0 && (
                <FilmDetail.Section>
                  <FilmDetail.SectionTitle>Highlight của phim</FilmDetail.SectionTitle>
                  <FilmDetail.RecommendedFilms>
                    {film.highlights.map((highlight) => (
                      <FilmDetail.FilmCard
                        key={highlight.id}
                        onClick={() =>
                          handleShortVideoClick({
                            id: highlight.id,
                            slug: film.slug,
                            title: highlight.title,
                            description: highlight.description,
                            thumbnail: highlight.thumbnail || bannerImage,
                            videoUrl: film.videoUrl,
                            scenes: highlight.scenes || [],
                          })
                        }
                      >
                        <FilmDetail.FilmPoster src={highlight.thumbnail || bannerImage} alt={highlight.title} />
                        <FilmDetail.FilmTitle>
                          {Math.floor(highlight.startTime / 60)}:{String(highlight.startTime % 60).padStart(2, '0')} -{' '}
                          {Math.floor(highlight.endTime / 60)}:{String(highlight.endTime % 60).padStart(2, '0')}
                        </FilmDetail.FilmTitle>
                        <FilmDetail.FilmMeta>{highlight.title}</FilmDetail.FilmMeta>
                      </FilmDetail.FilmCard>
                    ))}
                  </FilmDetail.RecommendedFilms>
                </FilmDetail.Section>
              )}

              {/* Shorts ngắn */}
              {film.shorts && film.shorts.length > 0 && (
                <FilmDetail.Section>
                  <FilmDetail.SectionTitle>Shorts (face-swap)</FilmDetail.SectionTitle>
                  <FilmDetail.RecommendedFilms>
                    {film.shorts.map((short, index) => (
                      <FilmDetail.FilmCard
                        key={`short-${index}`}
                        onClick={() => {
                          setSelectedShort(short);
                          setShowShortSwapPopup(true);
                        }}
                      >
                        <div style={{ position: 'relative', width: '100%' }}>
                          <FilmDetail.FilmPoster
                            src={short.thumbnail}
                            alt={`Short ${index + 1}`}
                            style={{
                              border: '1px solid #777',
                              borderRadius: '18px',
                            }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              top: '8px',
                              right: '8px',
                              background: 'rgba(68, 228, 28, 0.81)',
                              color: '#fff',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '700',
                            }}
                          >
                            {short.endTime - short.startTime}s
                          </div>
                        </div>
                        <FilmDetail.FilmTitle>
                          {Math.floor(short.startTime / 60)}:{String(short.startTime % 60).padStart(2, '0')} -{' '}
                          {Math.floor(short.endTime / 60)}:{String(short.endTime % 60).padStart(2, '0')}
                        </FilmDetail.FilmTitle>
                      </FilmDetail.FilmCard>
                    ))}
                  </FilmDetail.RecommendedFilms>
                </FilmDetail.Section>
              )}

              {/* Product Frames */}
              {film.frames && film.frames.length > 0 && (
                <FilmDetail.Section>
                  <FilmDetail.SectionTitle>Khung hình chứa sản phẩm</FilmDetail.SectionTitle>
                  <FilmDetail.RecommendedFilms>
                    {film.frames.map((frame, index) => (
                      <FilmDetail.FilmCard key={`frame-${index}`} onClick={() => handleFrameClick(frame)}>
                        <div style={{ position: 'relative', width: '100%' }}>
                          <FilmDetail.FilmPoster
                            src={frame.thumbnail}
                            alt={`Frame at ${formatTimestamp(frame.timestamp)}`}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              background: 'rgba(0, 0, 0, 0.6)',
                              backdropFilter: 'blur(8px)',
                              padding: '6px 12px',
                              borderRadius: '8px',
                              border: '2px solid rgba(21, 215, 27, 0.6)',
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.6)',
                            }}
                          >
                            <div
                              style={{
                                color: '#15D71B',
                                fontSize: '16px',
                                fontWeight: '700',
                                letterSpacing: '1px',
                                textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)',
                              }}
                            >
                              {formatTimestamp(frame.timestamp)}
                            </div>
                          </div>
                        </div>
                      </FilmDetail.FilmCard>
                    ))}
                  </FilmDetail.RecommendedFilms>
                </FilmDetail.Section>
              )}

              {/* Product List */}
              {allProducts.length > 0 && (
                <FilmDetail.Section>
                  <FilmDetail.SectionTitle>Danh sách sản phẩm</FilmDetail.SectionTitle>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '20px',
                      marginTop: '20px',
                    }}
                  >
                    {allProducts.map((product, index) => (
                      <div
                        key={`product-${index}`}
                        role="button"
                        tabIndex={0}
                        style={{
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)',
                          borderRadius: '12px',
                          overflow: 'hidden',
                          border: '1px solid rgba(255,255,255,0.1)',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-5px)';
                          e.currentTarget.style.borderColor = 'rgba(21,215,27,0.5)';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(21,215,27,0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        onClick={() => window.open(product.product_url, '_blank')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            window.open(product.product_url, '_blank');
                          }
                        }}
                      >
                        {/* Product Image */}
                        <div
                          style={{
                            width: '100%',
                            height: '200px',
                            overflow: 'hidden',
                            background: 'rgba(255,255,255,0.05)',
                          }}
                        >
                          <img
                            src={product.image_url}
                            alt={product.title}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              transition: 'transform 0.3s ease',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          />
                        </div>

                        {/* Product Info */}
                        <div
                          style={{
                            padding: '15px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              color: 'white',
                              fontSize: '14px',
                              fontWeight: '600',
                              lineHeight: '1.4',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              minHeight: '40px',
                            }}
                          >
                            {product.title}
                          </div>

                          <div
                            style={{
                              color: '#15D71B',
                              fontSize: '16px',
                              fontWeight: '700',
                              marginTop: 'auto',
                            }}
                          >
                            {product.price?.toLocaleString('vi-VN')}đ
                          </div>

                          <div
                            style={{
                              background: 'linear-gradient(135deg, #15D71B 0%, #0ea356 100%)',
                              color: 'white',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '700',
                              textAlign: 'center',
                              marginTop: '8px',
                            }}
                          >
                            Xem sản phẩm
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </FilmDetail.Section>
              )}

              {/* Related Films */}
              {relatedFilms.length > 0 && (
                <FilmDetail.Section>
                  <FilmDetail.SectionTitle>Cùng chủ đề</FilmDetail.SectionTitle>
                  <FilmDetail.RecommendedFilms>
                    {relatedFilms.map((relatedFilm) => (
                      <FilmDetail.FilmCard
                        key={relatedFilm._id}
                        onClick={() => history.push(`/film/${relatedFilm.slug}`)}
                      >
                        <FilmDetail.FilmPoster src={getThumbnail(relatedFilm)} alt={relatedFilm.title} />
                        <FilmDetail.FilmTitle>{relatedFilm.title}</FilmDetail.FilmTitle>
                        <FilmDetail.FilmMeta>
                          {relatedFilm.releaseDate ? new Date(relatedFilm.releaseDate).getFullYear() : '2024'} •{' '}
                          {relatedFilm.theme?.[0] || 'Phim'}
                        </FilmDetail.FilmMeta>
                      </FilmDetail.FilmCard>
                    ))}
                  </FilmDetail.RecommendedFilms>
                </FilmDetail.Section>
              )}
            </FilmDetail.InfoColumn>

            <FilmDetail.PosterColumn>
              <FilmDetail.Poster src={bannerImage} alt={film.title} />
            </FilmDetail.PosterColumn>
          </FilmDetail.MainContent>
        </FilmDetail.Content>
      </FilmDetail>

      <FooterContainer />

      {/* Short Video Popup */}
      <ShortVideoPopup
        isOpen={showShortVideoPopup}
        onClose={() => setShowShortVideoPopup(false)}
        selectedVideo={selectedShortVideo}
        relatedVideos={
          film.highlights?.map((highlight) => ({
            id: highlight.id,
            title: highlight.title,
            description: highlight.description,
            thumbnail: highlight.thumbnail || bannerImage,
            videoUrl: film.videoUrl,
          })) || []
        }
        ads={[]}
        onVideoClick={handleShortVideoClick}
      />

      {/* Product Frame Popup */}
      <ProductFramePopup
        isOpen={showProductFramePopup}
        onClose={() => setShowProductFramePopup(false)}
        frame={selectedFrame}
        videoSlug={film.slug}
      />

      {/* Short Swap Face Popup */}
      <ShortSwapPopup isOpen={showShortSwapPopup} onClose={() => setShowShortSwapPopup(false)} short={selectedShort} />
    </>
  );
}
